import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Formik, Form } from "formik";
import InputField from "../../../components/inputFields/InputField";
import Button from "../../../components/buttons/Button";
import FormikDropdown from "../../../components/inputFields/FormikDropDown";
import Breadcrumbs from "../../../components/BreadCrumbs";
import StyledHeadingText from "../../../components/Fonts/StyledHeadingText";
import { getHudPatientsHistory, patchHudPatientsHistory, postMedicineChanges } from "../../../services/hud/hud_patients";
import { toast } from "react-toastify";
import FormikMultiSelectDropdown from "../../../components/inputFields/FormikMultiSelectDropdown";
import CustomLoading from "../../../components/customLoading/CustomLoading";
import { getHudOintments, getHudTablets } from "../../../services/hud/hud_medicines";
import { hoThyroidOptions } from "../../../constants/h_o_thyroid_options";
import { formatDateTime } from "../../../utils/FormatDateTime";
import ShowModal from "../../../components/Hud/ShowModal";
import PatientMedicineView from "./patient_medicine_view";

const PatientHistory = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const [isMedicineModalOpen, setIsMedicineModalOpen] = useState(false);
    const [medicineModalOpenFor, setIsMedicineModalOpenFor] = useState("");

    const [initialValues, setInitialValues] = useState({
        "patient_history_id": "",
        "duration": "",
        "thyroid": "",
        "auto_imm_dis": "",
        "jaundice": "",
        "fh_auto_imm_dis": "",
        "ophthalmological_problems": "",
        "hospitalization": "",
        "psychiatric_history": "",
        "keloidol_tendency": "",
        "epilepsy": "",
        "blood_group": "",
        "steroid_history": "",
        "photo_of_date": "",
        "antibodies_to_thyroid": "",
        "h_o_epilepsy": "",
        "activity": "",
        "fh_b_vitiligo": "",
        "h_o_thyroid": "",
        "hb_value": "",
        "rating": "",
        "tablets": [],
        "ointments": [],
    });

    const [availableTablets, setAvailableTablets] = useState([]);
    const [availableOintments, setAvailableOintments] = useState([]);
    const [rowData, setRowData] = useState({});
    const [loading, setLoading] = useState(false);

    const openMedicineModal = (modelFor) => {
        setIsMedicineModalOpen(true);
        setIsMedicineModalOpenFor(modelFor);
    };

    // Close modal
    const closeMedicineModal = () => {
        setIsMedicineModalOpen(false);
        setIsMedicineModalOpenFor("");
    };

    useEffect(() => {
        const patientData = location.state;
        if (patientData) {
            setRowData(patientData);
            fetchAvailableTablets();
            fetchAvailableOintments();
            fetchData(patientData.patient_id);
        } else {
            toast.error("Error while looking for patient");
            navigate("/hud-patients");
        }
    }, [location.state]);

    const fetchData = async (patientId) => {
        setLoading(true);
        try {
            let res = await getHudPatientsHistory(patientId);
            if (res.status === 200) {
                setInitialValues(res.data);
            } else {
                toast.error("Unable to fetch patient details");
            }
        } catch (error) {
            console.error("Unable to fetch patient details:", error);
        } finally {
            setLoading(false);
        }
    };

    const fetchAvailableTablets = async () => {
        try {
            let res = await getHudTablets();
            if (res.status === 200) {
                setAvailableTablets(res.data);
            } else {
                toast.error("Unable to fetch Tablets");
            }
        } catch (error) {
            console.error("Unable to fetch Medicines:", error);
        }
    };

    const fetchAvailableOintments = async () => {
        try {
            let res = await getHudOintments();
            if (res.status === 200) {
                setAvailableOintments(res.data);
            } else {
                toast.error("Unable to fetch Tablets");
            }
        } catch (error) {
            console.error("Unable to fetch Medicines:", error);
        }
    };

    const handleSubmit = async (values, { setSubmitting }) => {
        setSubmitting(true);
        handleMedicinesUpdate(values);
        try {
            let updatedValues = {
                ...values,
                fh_b_vitiligo: `${values.fh_b_vitiligo}`,
                activity: `${values.activity}`,
            };
            const response = await patchHudPatientsHistory(rowData.patient_id, updatedValues);
            if (response?.status === 200) {
                toast.success("History updated successfully!");
            } else {
                toast.error("Oops! Something went wrong, please try again.");
            }
        } catch (error) {
            let errors =
                error?.response?.data?.error ||
                "Oops! Something went wrong, please try again.";
            toast.error(errors);
        } finally {
            setSubmitting(false);
        }
    };

    const handleMedicinesUpdate = async (values) => {
        try {
            const response = await postMedicineChanges({
                medicines: [...values.tablets, ...values.ointments],
            }, rowData.patient_id);

            if (response?.status === 200) {
                toast.success("Medicines updated successfully!");
            } else {
                let errors = "Oops! Something went wrong, please try again.";
                toast.error(errors);
            }
        } catch (error) {
            let errors =
                error?.response?.data?.error ||
                "Oops! Something went wrong, please try again.";
            toast.error(errors);
        }
    };

    return (
        loading ? <CustomLoading /> :
            <div>
                <div className="flex flex-row justify-between mb-2">
                    <div className="flex flex-row gap-2">
                        <StyledHeadingText size={"text-xs"} text={"Patient Details: "} />
                        <StyledHeadingText size={"text-xs"} TextColor="text-red-600" text={`${rowData.full_name} (${"Age: " + rowData.age || "DOB: " + rowData.date_of_birth} | Gen: ${rowData.gender})`} />
                        <StyledHeadingText size={"text-xs"} text={" | "} />
                        <StyledHeadingText size={"text-xs"} text={"Registration Date: "} />
                        <StyledHeadingText size={"text-xs"} TextColor="text-red-600" text={formatDateTime(rowData.date)} />
                    </div>
                    <Breadcrumbs items={[
                        { name: 'Home', link: '/invoice' },
                        { name: 'Patients', link: '/hud-patients' },
                        { name: 'Patient History' },
                    ]} />
                </div>
                <Formik
                    initialValues={initialValues}
                    onSubmit={handleSubmit}
                    enableReinitialize
                >
                    {({ isSubmitting, setFieldValue, values }) => (
                        <Form className="space-y-4">
                            <div className="grid gap-1 md:grid-cols-3 lg:grid-cols-4">
                                <InputField
                                    label="Duration"
                                    name="duration"
                                    type="text"
                                    placeholder="XX"
                                />
                                <FormikDropdown
                                    label="Thyroid"
                                    name="thyroid"
                                    placeholder="Select"
                                    needSearch
                                    options={[
                                        { value: "Negative", label: "Negative" },
                                        { value: "Positive", label: "Positive" },
                                        { value: "Kobe Negative", label: "(Kobe negative)" },
                                        { value: "Kobe Positive", label: "(Kobe positive)" },
                                        { value: "In relation father & mother", label: "(In relation father & mother)" },
                                        { value: "Father", label: "(Father)" },
                                        { value: "Mother", label: "(Mother)" },
                                        { value: "Sister", label: "(Sister)" },
                                        { value: "Positive Father", label: "(Positive father)" },
                                        { value: "Positive Mother", label: "(Positive mother)" },
                                    ]}
                                />
                                <FormikDropdown
                                    label="H/O Auto Immune Disease"
                                    name="auto_imm_dis"
                                    placeholder="Select"
                                    needSearch
                                    options={[
                                        { value: "Negative", label: "Negative" },
                                        { value: "Positive", label: "Positive" },
                                        { value: "Kobe Negative", label: "(Kobe negative)" },
                                        { value: "Kobe Positive", label: "(Kobe positive)" },
                                        { value: "In relation father & mother", label: "(In relation father & mother)" },
                                        { value: "Father", label: "(Father)" },
                                        { value: "Mother", label: "(Mother)" },
                                        { value: "Sister", label: "(Sister)" },
                                        { value: "Positive Father", label: "(Positive father)" },
                                        { value: "Positive Mother", label: "(Positive mother)" },
                                    ]}
                                />
                                <FormikDropdown
                                    label="H/O Jaundice"
                                    name="jaundice"
                                    placeholder="Select"
                                    needSearch
                                    options={[
                                        { value: "Negative", label: "Negative" },
                                        { value: "Positive", label: "Positive" },
                                        { value: "Kobe Negative", label: "(Kobe negative)" },
                                        { value: "Kobe Positive", label: "(Kobe positive)" },
                                        { value: "In relation father & mother", label: "(In relation father & mother)" },
                                        { value: "Father", label: "(Father)" },
                                        { value: "Mother", label: "(Mother)" },
                                        { value: "Sister", label: "(Sister)" },
                                        { value: "Positive Father", label: "(Positive father)" },
                                        { value: "Positive Mother", label: "(Positive mother)" },
                                    ]}
                                />
                                <FormikDropdown
                                    label="F/H (A) Auto Imm. Dis."
                                    name="fh_auto_imm_dis"
                                    placeholder="Select"
                                    needSearch
                                    options={[
                                        { value: "Negative", label: "Negative" },
                                        { value: "Positive", label: "Positive" },
                                        { value: "Kobe Negative", label: "(Kobe negative)" },
                                        { value: "Kobe Positive", label: "(Kobe positive)" },
                                        { value: "In relation father & mother", label: "(In relation father & mother)" },
                                        { value: "Father", label: "(Father)" },
                                        { value: "Mother", label: "(Mother)" },
                                        { value: "Sister", label: "(Sister)" },
                                        { value: "Positive Father", label: "(Positive father)" },
                                        { value: "Positive Mother", label: "(Positive mother)" },
                                    ]}
                                />
                                <FormikDropdown
                                    label="Ophthalmological Problems"
                                    name="ophthalmological_problems"
                                    placeholder="Select"
                                    needSearch
                                    options={[
                                        { value: "Negative", label: "Negative" },
                                        { value: "Positive", label: "Positive" },
                                        { value: "Kobe Negative", label: "(Kobe negative)" },
                                        { value: "Kobe Positive", label: "(Kobe positive)" },
                                        { value: "In relation father & mother", label: "(In relation father & mother)" },
                                        { value: "Father", label: "(Father)" },
                                        { value: "Mother", label: "(Mother)" },
                                        { value: "Sister", label: "(Sister)" },
                                        { value: "Positive Father", label: "(Positive father)" },
                                        { value: "Positive Mother", label: "(Positive mother)" },
                                    ]}
                                />
                                <FormikDropdown
                                    label="H/O Hospitalization"
                                    name="hospitalization"
                                    placeholder="Select"
                                    needSearch
                                    options={[
                                        { value: "Negative", label: "Negative" },
                                        { value: "Positive", label: "Positive" },
                                        { value: "Kobe Negative", label: "(Kobe negative)" },
                                        { value: "Kobe Positive", label: "(Kobe positive)" },
                                        { value: "In relation father & mother", label: "(In relation father & mother)" },
                                        { value: "Father", label: "(Father)" },
                                        { value: "Mother", label: "(Mother)" },
                                        { value: "Sister", label: "(Sister)" },
                                        { value: "Positive Father", label: "(Positive father)" },
                                        { value: "Positive Mother", label: "(Positive mother)" },
                                    ]}
                                />
                                <FormikDropdown
                                    label="Psychiatric History"
                                    name="psychiatric_history"
                                    placeholder="Select"
                                    needSearch
                                    options={[
                                        { value: "Negative", label: "Negative" },
                                        { value: "Positive", label: "Positive" },
                                        { value: "Kobe Negative", label: "(Kobe negative)" },
                                        { value: "Kobe Positive", label: "(Kobe positive)" },
                                        { value: "In relation father & mother", label: "(In relation father & mother)" },
                                        { value: "Father", label: "(Father)" },
                                        { value: "Mother", label: "(Mother)" },
                                        { value: "Sister", label: "(Sister)" },
                                        { value: "Positive Father", label: "(Positive father)" },
                                        { value: "Positive Mother", label: "(Positive mother)" },
                                    ]}
                                />
                                <FormikDropdown
                                    label="Keloidol Tendency"
                                    name="keloidol_tendency"
                                    placeholder="Select"
                                    needSearch
                                    options={[
                                        { value: "Negative", label: "Negative" },
                                        { value: "Positive", label: "Positive" },
                                        { value: "Kobe Negative", label: "(Kobe negative)" },
                                        { value: "Kobe Positive", label: "(Kobe positive)" },
                                        { value: "In relation father & mother", label: "(In relation father & mother)" },
                                        { value: "Father", label: "(Father)" },
                                        { value: "Mother", label: "(Mother)" },
                                        { value: "Sister", label: "(Sister)" },
                                        { value: "Positive Father", label: "(Positive father)" },
                                        { value: "Positive Mother", label: "(Positive mother)" },
                                    ]}
                                />
                                <FormikDropdown
                                    label="Epilepsy"
                                    name="epilepsy"
                                    placeholder="Select"
                                    needSearch
                                    options={[
                                        { value: "Negative", label: "Negative" },
                                        { value: "Positive", label: "Positive" },
                                        { value: "Kobe Negative", label: "(Kobe negative)" },
                                        { value: "Kobe Positive", label: "(Kobe positive)" },
                                        { value: "In relation father & mother", label: "(In relation father & mother)" },
                                        { value: "Father", label: "(Father)" },
                                        { value: "Mother", label: "(Mother)" },
                                        { value: "Sister", label: "(Sister)" },
                                        { value: "Positive Father", label: "(Positive father)" },
                                        { value: "Positive Mother", label: "(Positive mother)" },
                                    ]}
                                />
                                <FormikDropdown
                                    label="Blood Group"
                                    name="blood_group"
                                    placeholder="Select"
                                    needSearch
                                    options={[
                                        { value: "A+", label: "A+" },
                                        { value: "A-", label: "A-" },
                                        { value: "B+", label: "B+" },
                                        { value: "B-", label: "B-" },
                                        { value: "O+", label: "O+" },
                                        { value: "O-", label: "O-" },
                                        { value: "AB+", label: "AB+" },
                                        { value: "AB-", label: "AB-" },
                                    ]}
                                />
                                <InputField
                                    label="HB"
                                    name="hb_value"
                                    type="text"
                                    placeholder="XX"
                                />
                                <FormikDropdown
                                    label="History of steroids"
                                    name="steroid_history"
                                    placeholder="Select"
                                    needSearch
                                    options={[
                                        { value: "Yes", label: "Yes" },
                                        { value: "No", label: "No" },
                                    ]}
                                />
                                <InputField
                                    label="Photo Date"
                                    name="photo_of_date"
                                    type="date"
                                    placeholder="V"
                                    disable
                                />
                                <FormikDropdown
                                    label="Antibodies to Thyroid"
                                    name="antibodies_to_thyroid"
                                    placeholder="Select"
                                    needSearch
                                    options={[
                                        { value: "Yes", label: "Yes" },
                                        { value: "No", label: "No" },
                                    ]}
                                />
                                <FormikDropdown
                                    label="H/O Epilepsy"
                                    name="h_o_epilepsy"
                                    placeholder="Select"
                                    needSearch
                                    options={[
                                        { value: "Yes", label: "Yes" },
                                        { value: "No", label: "No" },
                                    ]}
                                />
                            </div>
                            <div className="grid gap-1 md:grid-cols-3">
                                <FormikMultiSelectDropdown
                                    label="Activity (Koebnerisation)"
                                    name="activity"
                                    needSearch
                                    placeholder="Select Activity (Koebnerisation)"
                                    options={[
                                        { value: "Negative", label: "Negative" },
                                        { value: "Positive", label: "Positive" },
                                        { value: "Kobe Negative", label: "(Kobe negative)" },
                                        { value: "Kobe Positive", label: "(Kobe positive)" },
                                        { value: "In relation father & mother", label: "(In relation father & mother)" },
                                        { value: "Father", label: "(Father)" },
                                        { value: "Mother", label: "(Mother)" },
                                        { value: "Sister", label: "(Sister)" },
                                        { value: "Positive Father", label: "(Positive father)" },
                                        { value: "Positive Mother", label: "(Positive mother)" },
                                    ]}
                                />
                                <FormikMultiSelectDropdown
                                    label="F/H (B) Vitiligo"
                                    name="fh_b_vitiligo"
                                    needSearch
                                    placeholder="Select F/H (B) Vitiligo"
                                    options={[
                                        { value: "Negative", label: "Negative" },
                                        { value: "Positive", label: "Positive" },
                                        { value: "Kobe Negative", label: "(Kobe negative)" },
                                        { value: "Kobe Positive", label: "(Kobe positive)" },
                                        { value: "In relation father & mother", label: "(In relation father & mother)" },
                                        { value: "Father", label: "(Father)" },
                                        { value: "Mother", label: "(Mother)" },
                                        { value: "Sister", label: "(Sister)" },
                                        { value: "Positive Father", label: "(Positive father)" },
                                        { value: "Positive Mother", label: "(Positive mother)" },
                                    ]}
                                />
                                <FormikDropdown
                                    label="H/O Thyroid"
                                    name="h_o_thyroid"
                                    placeholder="Select"
                                    needSearch
                                    options={hoThyroidOptions.map((item) => ({
                                        value: item,
                                        label: item,
                                    }))}
                                />
                            </div>
                            <div className="grid gap-1 grid-cols-1 md:grid-cols-2">
                                <div className="flex flex-row items-end">
                                    <div className="flex-1">
                                        <FormikMultiSelectDropdown
                                            label="Tablets"
                                            name="tablets"
                                            needSearch
                                            placeholder="Select Tablets"
                                            options={availableTablets.map((item) => ({
                                                value: item.medicine_id,
                                                label: item.name,
                                            }))}
                                        />
                                    </div>
                                    <div
                                        className="text-white bg-primary hover:bg-primary-dark font-sm text-xs px-8 py-1.5 text-center max-h-12 mb-2 rounded cursor-pointer"
                                        onClick={() => openMedicineModal("tablet")}
                                    >
                                        View
                                    </div>
                                </div>
                                <div className="flex flex-row items-end">
                                    <div className="flex-1">
                                        <FormikMultiSelectDropdown
                                            label="Ointment-Lotion"
                                            name="ointments"
                                            needSearch
                                            placeholder="Select Ointment-Lotion"
                                            options={availableOintments.map((item) => ({
                                                value: item.medicine_id,
                                                label: item.name,
                                            }))}
                                        />
                                    </div>
                                    <div
                                        className="text-white bg-primary hover:bg-primary-dark font-sm text-xs px-8 py-1.5 text-center max-h-12 mb-2 rounded cursor-pointer"
                                        onClick={() => openMedicineModal("ointment")}
                                    >
                                        View
                                    </div>
                                </div>
                            </div>

                            <div className="flex gap-4">
                                <Button
                                    type="submit"
                                    className="bg-primary text-white px-4 py-2 rounded text-xs"
                                    disabled={isSubmitting}
                                >
                                    Update
                                </Button>
                                <Button
                                    type="button"
                                    className="bg-gray-500 text-white px-4 py-2 rounded text-xs"
                                    onClick={() => navigate(-1)}
                                >
                                    Back
                                </Button>
                            </div>
                        </Form>
                    )}
                </Formik>
                <ShowModal modalWidth={"50%"} contentLabel={"View Medcine alert"} isOpen={isMedicineModalOpen} onRequestClose={closeMedicineModal} componentToPass={<PatientMedicineView openedFor={medicineModalOpenFor} patientId={rowData.patient_id} />} />
            </div >
    );
};

export default PatientHistory;
